import { useState } from 'react';
import { X } from 'lucide-react';
import { useGenerateInvite } from '@/hooks/useGroupInvites';
import { copyToClipboard } from '@/lib/utils';
import { toast } from 'sonner';
import { T, RADIUS, SHADOW } from '@/lib/tokens';
import { INVITE_STEP_SKIPPED_KEY, getAppUrl } from '@/lib/constants';

interface InviteReminderBannerProps {
  groupId: string;
  memberCount: number;
}

/**
 *
 * @param root0
 * @param root0.groupId
 * @param root0.memberCount
 */
export function InviteReminderBanner({ groupId, memberCount }: InviteReminderBannerProps) {
  const [dismissed, setDismissed] = useState(false);
  const generateInvite = useGenerateInvite();

  // Only show if user skipped invite and group has 1 member
  const skippedKey = `${INVITE_STEP_SKIPPED_KEY}_${groupId}`;
  const skipped = localStorage.getItem(skippedKey) === 'true';
  if (!skipped) return null;
  if (dismissed) return null;

  // Auto-clear the flag when condition is met
  if (memberCount >= 2) {
    localStorage.removeItem(skippedKey);
    return null;
  }

  const handleCopyInvite = async () => {
    try {
      const invite = await generateInvite.mutateAsync({ groupId });
      const link = `${getAppUrl()}/invite/${invite.invite_code}`;
      await copyToClipboard(link);
      toast.success('Invite link copied!');
    } catch {
      // Error toast handled by useGenerateInvite onError
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '12px 14px',
        background: T.warningSoft,
        border: `1px solid ${T.warningBorder}`,
        borderRadius: RADIUS.lg,
        marginBottom: 12,
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ fontSize: 13, color: T.text, margin: 0, lineHeight: 1.5 }}>
          Your group is empty — invite members to get started.
        </p>
      </div>
      <button
        onClick={handleCopyInvite}
        disabled={generateInvite.isPending}
        style={{
          padding: '7px 14px',
          borderRadius: RADIUS.sm,
          background: T.accent,
          color: T.white,
          border: 'none',
          fontSize: 13,
          fontWeight: 600,
          cursor: generateInvite.isPending ? 'default' : 'pointer',
          fontFamily: 'inherit',
          whiteSpace: 'nowrap',
          boxShadow: SHADOW.btn.accent,
          opacity: generateInvite.isPending ? 0.7 : 1,
        }}
      >
        {generateInvite.isPending ? 'Creating…' : 'Copy Invite Link'}
      </button>
      <button
        onClick={() => setDismissed(true)}
        aria-label="Dismiss"
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          color: T.textMuted,
          padding: 4,
          display: 'flex',
          alignItems: 'center',
          flexShrink: 0,
        }}
      >
        <X size={16} />
      </button>
    </div>
  );
}
